import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Event as NavigationEvent, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';
import { EmitType } from '@syncfusion/ej2-base';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AppGlobals } from '../../shared/globals/appGlobals';
import { IApplicationSettings } from '../../shared/models/app-settings.model';
import { ICustomer, ICustomerApplicationVariant, IHasPermissionObject, IPermission } from '../../shared/models/index';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { ApiCredentialsService, AuthorizationService, CustomerService, StorageManagerService } from '../../shared/services/index';
import { AbsoluteSessionTimeoutService } from '../../shared/services/session-timeout-absolute.service';
import { SessionTimeoutService } from '../../shared/services/session-timeout.service';
import { CompanyDatascopeSelectorComponent } from '../company-datascope-selector//company-datascope-selector.component';
import { HomeComponent } from '../home/home.component';
import { CustomerSelectorComponent, FooterComponent, NavMenuComponent } from '../index';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  position: object = { X: 'center', Y: 'center' };
  modalWidth = '25%';
  modalHeight = 'auto';
  animationSettings: Object = { effect: 'Zoom' };
  isModal = true;
  sessionExpireSecondsLeft: number;
  absoluteSessionExpireMinutesLeft: number;
  hidden = false;

  title = 'app';
  isIframe: boolean;
  lastLogonDate: string;
  hasPermission: boolean;
  hasCurrentCustomer: boolean;
  userName: string;
  permissionsSet = [];
  permissionsPerClient = [];
  permissionsPerClientObjectSet: IPermission[] = [];
  selectedCustomer: string;
  subscription: Subscription;
  currentCustomer: ICustomer;
  email: string;
  loggedIn = false;
  customerId: number;
  userGuid: string;
  customers: ICustomer[] = [];
  eligibleCustomers: any[] = [];
  dataScope: string;
  dataSourceApi: any;
  dataScopeId: number;
  dataSource: any[] = [];
  applications: any[] = [];
  customersDataSource: any[] = [];
  activeLink: string;
  permissionsConst: any;
  clientLanguageEnabled = false;

  private applicationName = 'SingleSource';

  @ViewChild('customerSelector')
  customerSelector: CustomerSelectorComponent;

  @ViewChild('navMenu', { static: true })
  navMenuComponent: NavMenuComponent;

  @ViewChild('footer', { static: true })
  footerComponent: FooterComponent;

  @ViewChild('sessionTimeoutModal', { static: true })
  public sessionTimeoutModal: DialogComponent;

  @ViewChild('absoluteSessionTimeoutModal', { static: true })
  public absoluteSessionTimeoutModal: DialogComponent;

  @ViewChild('companyDatascopeSelectorComponent')
  companyDatascopeSelectorComponent: CompanyDatascopeSelectorComponent;

  hasPermissionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  lastLogonDateSourceSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  homeComponent: HomeComponent;

  dataScopeName: any;
  loading: boolean;
  customer: ICustomer;
  companyDialogVisible = false;
  showDataScopeSelection = false;
  navigateTo = '';
  applicationSettings: IApplicationSettings;

  constructor (private authorizationService: AuthorizationService, private storageManagerService: StorageManagerService,
    private authenticationService: AuthenticationService, private customerService: CustomerService, private router: Router,
    private sessionTimeoutservice: SessionTimeoutService, private absoluteSessionTimeoutService: AbsoluteSessionTimeoutService, private appGlobals: AppGlobals, private apiService: ApiCredentialsService) {

    // Initialize datadog RUM
    authenticationService.initDataDogScriptElement()
      .then(() => {
        authenticationService.initDataDogRUM();
      })
      .catch(); // ignore the exceptions here: There is a chance that you might see "ERROR Error: Uncaught (in promise): ChunkLoadError: Loading chunk 1 failed." when datadog script type is set to "text/plain"


    router.events
      .subscribe((routerEvent: NavigationEvent) => {
        if (routerEvent instanceof NavigationStart) {

          this.activeLink = routerEvent.url;
          this.loading = true;
        }
        if (routerEvent instanceof NavigationEnd ||
          routerEvent instanceof NavigationCancel ||
          routerEvent instanceof NavigationError) {

          this.loading = false;
        }
      });

    //  This is to avoid reload during acquireTokenSilent() because of hidden iframe
    this.isIframe = window !== window.parent && !window.opener;

    // Handle the browser refresh
    if (this.storageManagerService.read('lastLogonDate')) {
      this.lastLogonDate = this.storageManagerService.read('lastLogonDate');
      this.authorizationService.lastLogonDateSourceSubject.next(this.lastLogonDate);
    }

    if (this.storageManagerService.read('hasPermission')) {
      this.hasPermission = this.storageManagerService.read('hasPermission');
      this.authorizationService.hasPermissionSubject.next(this.hasPermission);
    }

    this.permissionsSet = this.storageManagerService.read('accessPermissions');
    this.permissionsConst = this.appGlobals.permissions;
  }

  onActivate(componentRef) {

    if (componentRef instanceof HomeComponent) {

      this.homeComponent = componentRef;
      this.navMenuComponent.activeLink = '/';
      return;
    }
  }

  ngOnInit() {

    this.subscription = this.authenticationService.getUserData().catch((err) =>
      this.handleError(err)).subscribe((userData: any) => {
        if (userData) {

          const cust = this.storageManagerService.read('currentCustomer');
          if (cust) {
            this.currentCustomer = typeof cust === 'object' ? cust : JSON.parse(cust);

            this.authenticationService.initPendo(userData.userguid, this.currentCustomer.customerName);
          }

          this.userName = userData.name;
          this.email = userData.preferred_username;

          if (userData.applications) {
            this.applications = JSON.parse(userData.applications);
          }

          if (this.homeComponent) {
            this.homeComponent.setUserName(this.userName);
            this.homeComponent.customer = this.currentCustomer;
          }

          if (this.applications && this.applications.length > 0) {

            if (!this.storageManagerService.read('userGuid')) {

              this.subscription = this.authorizationService.getPermisions(userData.preferred_username)
                .subscribe((res: IHasPermissionObject) => {

                  const hasApplicationPermissions = res.hasApplicationPermissions;

                  this.hasPermissionSubject.next(hasApplicationPermissions);
                  this.hasPermission = hasApplicationPermissions;
                  this.authorizationService.hasPermissionSubject.next(this.hasPermission);
                  this.storageManagerService.write('hasPermission', hasApplicationPermissions);
                  this.lastLogonDateSourceSubject.next(userData.lastlogondate);
                  this.storageManagerService.write('lastLogonDate', userData.lastlogondate);
                  this.storageManagerService.write('userGuid', res.userGuid);

                  if (!hasApplicationPermissions) {
                    this.storageManagerService.write('accessDeniedEmail', this.email);
                    this.router.navigate(['/accessdenied']).then(() => {
                      return;
                    });
                  } else {

                    this.subscription = this.getCustomers().catch((err) =>
                      this.handleError(err)).subscribe(data => {

                        this.subscription = this.getCustomerApplicationVariants().catch((err) =>
                          this.handleError(err)).subscribe(appVars => {
                            this.customers = data;

                            if (appVars && appVars.length) {
                              appVars.sort((a, b) => a.applicationVariantName.localeCompare(b.applicationVariantName));
                            }

                            if (this.customers && this.customers.length > 0) {

                              this.eligibleCustomers = [];

                              this.customers.forEach(cu => {
                                if (cu['activeFlag'] === true) {
                                  const c = {};
                                  c['customerId'] = cu['customerId'];
                                  c['customerGuid'] = cu['customerGuid'];
                                  c['customerName'] = cu['customerName'];
                                  c['applicationVariants'] = appVars
                                    .filter(appVar => appVar.customerGuid === cu['customerGuid'])
                                    .map(v => {
                                      return {
                                        applicationVariantName: v.applicationVariantName,
                                        applicationVariantId: v.applicationVariantId
                                      };
                                    });
                                  if (c['applicationVariants'].length > 0) {
                                    this.eligibleCustomers.push(c);
                                  }
                                }
                              });

                              if (this.eligibleCustomers && this.eligibleCustomers.length) {
                                this.eligibleCustomers.sort((a, b) => a.customerName.localeCompare(b.customerName));
                              }

                              this.storageManagerService.write('eligibleCustomers', this.eligibleCustomers);

                              if (this.eligibleCustomers.length === 1) {

                                this.storageManagerService.write('currentCustomer', this.eligibleCustomers[0]);
                                this.homeComponent.customer = this.eligibleCustomers[0];

                                if (this.eligibleCustomers[0].applicationVariants.length === 1) {
                                  this.storageManagerService.write('dataScope', this.eligibleCustomers[0].applicationVariants[0].applicationVariantName.toLowerCase());
                                }

                                if (!this.permissionsSet || !this.permissionsSet.length) {

                                  this.subscription = this.authorizationService.setAccessPermissions(this.email).subscribe(permissionsRes => {

                                    if (permissionsRes) {

                                      // setPermissions function writes to the storage, converts and sets permissionSet vars
                                      this.setPermissions(permissionsRes);


                                      this.subscription = this.customerService.getCompanySetting(this.currentCustomer.customerId.toString(), 'isLanguageEnabled').subscribe(response => {
                                        if (response && !response.IsError) {
                                          this.customerService.applicationSettings.next(response.data);
                                          this.storageManagerService.write('clientLanguageEnabled', response.data);
                                          this.clientLanguageEnabled = response.data;

                                          if (this.navMenuComponent) {

                                            this.navMenuComponent.hasCurrentCustomer = true;
                                            this.navMenuComponent.selectedCustomer = this.selectedCustomer;
                                            this.navMenuComponent.customers = this.eligibleCustomers;

                                            this.navMenuComponent.permissionsSet = this.permissionsSet;
                                            this.navMenuComponent.uploadFilesMenu = this.authorizationService.hasPermission(this.permissionsConst.CanUploadFile);
                                            this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);
                                            this.navMenuComponent.exportDataPermitted = this.authorizationService.exportDataPermission();
                                          }
                                        }
                                      });
                                    }
                                  });
                                }

                                this.customers = this.eligibleCustomers[0].customerId;
                                this.currentCustomer = this.eligibleCustomers[0];
                                if (this.navMenuComponent) {
                                  this.navMenuComponent.hasCurrentCustomer = true;
                                  this.navMenuComponent.selectedCustomer = this.eligibleCustomers[0].customerName;
                                }

                                this.subscription = this.customerService.getCompanySetting(this.currentCustomer.customerId.toString(), 'isLanguageEnabled').subscribe(response => {
                                  if (response && !response.IsError) {
                                    this.customerService.applicationSettings.next(response.data);
                                    this.storageManagerService.write('clientLanguageEnabled', response.data);
                                    this.clientLanguageEnabled = response.data;

                                    this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);
                                  }
                                });
                              }
                            }

                            if (this.currentCustomer) {

                              if (!this.permissionsSet || !this.permissionsSet.length) {

                                this.subscription = this.authorizationService.setAccessPermissions(userData.preferred_username).subscribe(resp => {

                                  this.dataScope = this.storageManagerService.read('dataScope');

                                  // process permission set according to the datascope
                                  this.setPermissions(resp);

                                  if (this.navMenuComponent) {
                                    this.navMenuComponent.permissionsSet = this.permissionsSet;
                                    this.navMenuComponent.uploadFilesMenu = this.authorizationService.hasPermission(this.permissionsConst.CanUploadFile);
                                  }
                                });
                              }
                            } else {
                              this.customers = this.eligibleCustomers;

                              if (this.customers && this.customers.length === 0) {
                                this.router.navigate(['/accessdenied']);
                              }
                            }
                          });
                      });
                  }
                });
            } else {
              if (!this.permissionsSet || this.permissionsSet.length === 0) {

                if (this.currentCustomer) {

                  this.subscription = this.customerService.getCompanySetting(this.currentCustomer.customerId.toString(), 'isLanguageEnabled').subscribe(response => {
                    if (response && !response.IsError) {
                      this.customerService.applicationSettings.next(response.data);
                      this.storageManagerService.write('clientLanguageEnabled', response.data);
                      this.clientLanguageEnabled = response.data;

                      const user = this.storageManagerService.read('userData');
                      if (user && user !== '""') {

                        const userName = typeof user === 'object' ? user.preferred_username : JSON.parse(user).preferred_username;

                        this.subscription = this.authorizationService.setAccessPermissions(userName).subscribe(res => {

                          this.dataScope = this.storageManagerService.read('dataScope');

                          this.setPermissions(res);

                          if (this.navMenuComponent) {
                            this.navMenuComponent.permissionsSet = this.permissionsSet;
                            this.navMenuComponent.uploadFilesMenu = this.authorizationService.hasPermission(this.permissionsConst.CanUploadFile);
                            this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);
                            this.navMenuComponent.exportDataPermitted = this.authorizationService.exportDataPermission();
                          }
                        });
                      }

                      this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);
                    }
                  });


                } else {

                  this.eligibleCustomers = this.storageManagerService.read('eligibleCustomers');

                  if (this.eligibleCustomers && this.eligibleCustomers.length > 0) {
                    this.customers = this.eligibleCustomers;
                  }
                }
              } else {

                this.subscription = this.customerService.getCompanySetting(this.currentCustomer.customerId.toString(), 'isLanguageEnabled').subscribe(response => {
                  if (response && !response.IsError) {
                    this.customerService.applicationSettings.next(response.data);
                    this.storageManagerService.write('clientLanguageEnabled', response.data);
                    this.clientLanguageEnabled = response.data;

                    this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);

                    if (this.navMenuComponent) {
                      this.navMenuComponent.permissionsSet = this.permissionsSet;
                      this.navMenuComponent.uploadFilesMenu = this.authorizationService.hasPermission(this.permissionsConst.CanUploadFile);
                      this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);
                      this.navMenuComponent.exportDataPermitted = this.authorizationService.exportDataPermission();
                    }
                  }
                });

              }
            }

            this.subscription = this.authenticationService.getIsAuthorized().subscribe(
              (isAuthorized: boolean) => {
                if (isAuthorized) {
                  this.absoluteSessionTimeoutService.reset();
                }
                if (this.navMenuComponent) {
                  this.navMenuComponent.loggedIn = isAuthorized;
                }
              });

            this.subscription = this.authorizationService.hasPermissionSubject.subscribe((res) => {
              this.hasPermission = res;
            });

            this.lastLogonDate = this.storageManagerService.read('lastLogonDate');

            if (!this.lastLogonDate) {

              this.subscription = this.lastLogonDateSourceSubject.subscribe(res => {
                this.lastLogonDate = res;

              });
            }

            // Session Time out
            this.subscription = this.sessionTimeoutservice.sessionTimeoutWarning.subscribe(time => {
              this.sessionExpireSecondsLeft = time / 1000;
              this.displaySessionExpireWarning();
            });
            this.sessionTimeoutservice.reset();

            // Absolute Session
            this.subscription = this.absoluteSessionTimeoutService.sessionTimeoutWarning.subscribe(time => {
              this.absoluteSessionExpireMinutesLeft = time / 60000;
              this.displayAbsoluteSessionExpireWarning();
            });
          } else {
            this.router.navigate(['/accessdenied']);
          }
        }
      });
  }

  dialogClicked(event) {

    if (event.target && event.target.type !== 'button') {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  ngAfterViewInit(): void {
  }

  getCustomers(): Observable<ICustomer[]> {

    this.userGuid = this.storageManagerService.read('userGuid');

    const paramsContainer = {
      applicationName: this.applicationName,
      userGuid: this.userGuid
    };

    return this.customerService.getCustomers(paramsContainer);
  }

  getCustomerApplicationVariants(): Observable<ICustomerApplicationVariant[]> {

    this.userGuid = this.storageManagerService.read('userGuid');

    const paramsContainer = {
      applicationName: this.applicationName,
      userGuid: this.userGuid
    };

    return this.customerService.getCustomerApplicationVariants(paramsContainer);
  }

  changeDataScope(dataScope: any) {

    this.dataScopeName = dataScope['name'];
  }

  receiveCustomer(customer: any) {

    this.dataSourceApi = [];
    this.permissionsPerClient = [];
    const oldCustomer = this.currentCustomer;

    this.currentCustomer = customer;
    this.selectedCustomer = customer.customerName;

    this.storageManagerService.write('currentCustomer', customer);
    this.storageManagerService.remove('userPrefs');

    this.dataScope = this.storageManagerService.read('dataScope');

    this.setApplicationSettings('isLanguageEnabled')

    // if Client is the same and only datascope has changed no need to call for a permissions, just filter out the existing objects set accordingly
    if (oldCustomer && (+this.currentCustomer.customerId === +oldCustomer.customerId)) {

      const permissions: IPermission[] = this.storageManagerService.read('accessPermissions');

      if (permissions && permissions.length) {

        this.setPermissions(permissions);
        this.navMenuComponent.dataScope = this.dataScope;
        this.navMenuComponent.hasCurrentCustomer = true;
        this.navMenuComponent.selectedCustomer = this.selectedCustomer;
        this.navMenuComponent.permissionsSet = this.permissionsSet;
        this.navMenuComponent.uploadFilesMenu = this.authorizationService.hasPermission(this.permissionsConst.CanUploadFile);

        if (this.homeComponent) {
          this.homeComponent.setUserName(this.userName);
          this.homeComponent.customer = this.currentCustomer;
        }

        if (customer.applicationVariants && customer.applicationVariants.length) {

          this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);

          this.navMenuComponent.exportDataPermitted = this.authorizationService.exportDataPermission();

          if (location.pathname.indexOf('/inventory') > -1 || location.pathname.indexOf('/fileupload') > -1
            || location.pathname.indexOf('/managesubmissionitems') > -1 || location.pathname.indexOf('/submissionhistory') > -1 || location.pathname.indexOf('/exportdata') > -1) {

            if ((this.currentCustomer.customerId !== oldCustomer.customerId) || (this.currentCustomer.customerId === oldCustomer.customerId && this.currentCustomer.applicationVariants.length > 1)) {

              if (!this.navigateTo || location.pathname === this.navigateTo) {
                window.location.reload();
              } else {
                this.router.navigate([this.navigateTo]);
              }
            }
          } else if (location.pathname.includes('/itemdetails') || location.pathname.includes('/submissionitemdetails') || location.pathname.includes('/submissionhistoryitem') || location.pathname.includes('/clientsettings')) {

            this.router.navigate(['/home']);
          }

        }
      }
    } else {

      // Client selection changed - get the permissions with channels
      this.subscription = this.authorizationService.setAccessPermissions(this.email).subscribe(res => {

        if (res) {

          this.setPermissions(res);

          // untill the Channel is selected the "per client" permission are in place for the "non-ha" specific pages
          if (this.navMenuComponent) {

            if (!this.eligibleCustomers || this.eligibleCustomers.length === 0) {
              this.eligibleCustomers = this.storageManagerService.read('eligibleCustomers');
            }

            if (!this.dataScope) {

              if (customer.applicationVariants && customer.applicationVariants.length === 1) {
                this.storageManagerService.write('dataScope', customer.applicationVariants[0].applicationVariantName.toLowerCase());

                this.dataScope = customer.applicationVariants[0].applicationVariantName.toLowerCase();
                this.permissionsSet = this.permissionsPerClient;
              }
            }

            this.navMenuComponent.dataScope = this.dataScope;

            this.navMenuComponent.hasCurrentCustomer = true;
            this.navMenuComponent.selectedCustomer = this.selectedCustomer;

            this.navMenuComponent.permissionsSet = this.permissionsSet;
            this.navMenuComponent.uploadFilesMenu = this.authorizationService.hasPermission(this.permissionsConst.CanUploadFile);
          }

          // can't move the below section out of the subscribe because it's getting set before the subscription is done
          if (this.homeComponent) {
            this.homeComponent.setUserName(this.userName);
            this.homeComponent.customer = this.currentCustomer;
          }

          if (customer.applicationVariants && customer.applicationVariants.length) {

            this.subscription = this.customerService.getCompanySetting(this.currentCustomer.customerId.toString(), 'isLanguageEnabled').subscribe(response => {
              if (response && !response.IsError) {
                this.customerService.applicationSettings.next(response.data);
                this.storageManagerService.write('clientLanguageEnabled', response.data);
                this.clientLanguageEnabled = response.data;

                this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);
              }
            });

            this.navMenuComponent.exportDataPermitted = this.authorizationService.exportDataPermission();

            if (location.pathname.includes('/inventory') || location.pathname.includes('/fileupload')
              || location.pathname.includes('/managesubmissionitems') || location.pathname.includes('/submissionhistory') || location.pathname.indexOf('/exportdata') > -1) {

              if ((this.currentCustomer.customerId !== oldCustomer.customerId) || (this.currentCustomer.customerId === oldCustomer.customerId && this.currentCustomer.applicationVariants.length > 1)) {

                if (!this.navigateTo || location.pathname === this.navigateTo) {
                  window.location.reload();
                } else {
                  this.router.navigate([this.navigateTo]);
                }
              }
            } else if (location.pathname.includes('/itemdetails') || location.pathname.includes('/submissionitemdetails') || location.pathname.includes('/submissionhistoryitem') || location.pathname.includes('/clientsettings')) {

              this.router.navigate(['/home']);
            }
          }

          if (oldCustomer && location.pathname === '/home' || location.pathname.includes('/exportdata') || location.pathname.includes('/fileupload') || location.pathname.includes('/support')) {

            window.location.reload();
          }
        }
      });
    }

    const userguid = this.storageManagerService.read('userGuid');
    this.authenticationService.initPendo(userguid, this.selectedCustomer);
  }
  setApplicationSettings(setting: string) {

    this.subscription = this.customerService.getCompanySetting(this.currentCustomer.customerId.toString(), setting).subscribe(response => {
      if (response && !response.IsError) {
        this.customerService.applicationSettings.next(response.data);
        this.storageManagerService.write('clientLanguageEnabled', response.data);
        this.clientLanguageEnabled = response.data;

        this.navMenuComponent.apiAccessPermitted = this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanAccessClientAdminSettings) || (this.authorizationService.hasPermissionPerClient(this.permissionsConst.CanEditLanguageSettings) && this.clientLanguageEnabled);
      }
    });
  }

  private setPermissions(res: any) {

    const permissions: IPermission[] = res;

    // store the original permissions aray of objects in the storage
    this.storageManagerService.write('accessPermissions', permissions);

    // create a set of unique permission strings PER CLIENT.
    this.permissionsPerClient = this.authorizationService.permissionsToStringObject(res);

    if (this.dataScope) {
      const permissionObjects = permissions.filter(p => p.applicationVariantName.toLowerCase() === this.dataScope.toLowerCase());

      if (permissionObjects) {
        this.permissionsSet = this.authorizationService.permissionsToStringObject(permissionObjects);
      }

    } else {
      this.permissionsSet = this.permissionsPerClient;
    }
  }

  displaySessionExpireWarning() {

    this.sessionTimeoutModal.show();
  }

  displayAbsoluteSessionExpireWarning() {
    this.absoluteSessionTimeoutModal.show();
  }

  public onOverlayClick: EmitType<object> = () => {

    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  public onContinueClicked(event) {
    this.sessionTimeoutModal.hide();
  }
  // tslint:disable-next-line: member-ordering
  dlgButtons: Object[] = [{
    click: this.onContinueClicked.bind(this), buttonModel: { content: 'Extend my session', isPrimary: true }
  }];

  public onAbsoluteContinueClicked(event) {
    this.absoluteSessionTimeoutModal.hide();
  }

  // tslint:disable-next-line: member-ordering
  dlgAbsoluteSessionTimeoutButtons: Object[] = [{
    click: this.onAbsoluteContinueClicked.bind(this), buttonModel: { content: 'OK', isPrimary: true }
  }];


  private handleError(error: any) {
    return Observable.throwError(error);
  }

  companyModalSelectionModal(value) {

    this.companyDialogVisible = value;
  }

  changeShowDataScopeSelection(value) {

    this.showDataScopeSelection = value;
  }

  navigateToPage(value) {

    this.navigateTo = value;
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
